import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Upload, Mic, ChevronDown, ChevronUp, Brain, AlertCircle,
  Loader2, Trash2, FileText, File, Shield, Zap, Target, Eye,
  MessageSquare, AlertTriangle, TrendingUp, Heart, Users, Layers,
  HelpCircle, ArrowRight, CheckCircle, BookOpen, Download, Send,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  type CoachingSession, type SessionAnalysis,
  type SeverityLevel, type ConfidenceLevel,
} from '../../lib/coaching-sessions';

interface Props {
  session: CoachingSession;
  coachId: string;
  clientName: string;
  clientGoals?: string;
  reportsUsed?: number;
  onClose: () => void;
  onUpdate: (sessionId: string, updates: Partial<CoachingSession>) => void;
  onReportUsed?: () => void;
}

type Step = 'upload' | 'processing' | 'results';
type InputMode = 'audio' | 'document' | 'notes_only';
type ResultsTab = 'analysis' | 'chat';

interface ChatMessage {
  role: 'coach' | 'ai';
  content: string;
}

// ─── Badge helpers ────────────────────────────────────────────────────────────

const SEVERITY_COLORS: Record<SeverityLevel, string> = {
  mild: 'bg-amber-50 text-amber-700 border-amber-200',
  notable: 'bg-orange-50 text-orange-700 border-orange-200',
  significant: 'bg-red-50 text-red-700 border-red-200',
};

const CONFIDENCE_COLORS: Record<ConfidenceLevel, string> = {
  low: 'bg-amber-50 text-amber-600 border-amber-200',
  medium: 'bg-teal-50 text-teal-700 border-teal-200',
  high: 'bg-sage-50 text-sage-700 border-sage-200',
};

const CONFIDENCE_COLORS_DARK: Record<ConfidenceLevel, string> = {
  low: 'bg-amber-400/20 text-amber-200 border-amber-400/40',
  medium: 'bg-white/20 text-white border-white/30',
  high: 'bg-white/25 text-white border-white/40',
};

const CONFIDENCE_DOT: Record<ConfidenceLevel, string> = {
  low: 'bg-amber-400',
  medium: 'bg-teal-500',
  high: 'bg-sage-500',
};

const CONFIDENCE_DOT_DARK: Record<ConfidenceLevel, string> = {
  low: 'bg-amber-300',
  medium: 'bg-white',
  high: 'bg-white',
};

function SeverityBadge({ severity }: { severity: SeverityLevel }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${SEVERITY_COLORS[severity]}`}>
      {severity}
    </span>
  );
}

function ConfidenceBadge({ confidence, dark }: { confidence: ConfidenceLevel; dark?: boolean }) {
  const colors = dark ? CONFIDENCE_COLORS_DARK : CONFIDENCE_COLORS;
  const dots = dark ? CONFIDENCE_DOT_DARK : CONFIDENCE_DOT;
  return (
    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${colors[confidence]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dots[confidence]}`} />
      {confidence}
    </span>
  );
}

// ─── Collapsible section ──────────────────────────────────────────────────────

function Section({ title, icon, count, children, defaultOpen = true, accent }: {
  title: string;
  icon: React.ReactNode;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  accent?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-cream-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 bg-cream-50 hover:bg-cream-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className={accent || 'text-teal-600'}>{icon}</span>
          <span className="text-sm font-semibold text-chocolate-800">{title}</span>
          {count !== undefined && count > 0 && (
            <span className="text-xs bg-cream-200 text-chocolate-500 px-1.5 py-0.5 rounded-full">{count}</span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-chocolate-400" /> : <ChevronDown className="w-4 h-4 text-chocolate-400" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Input mode tab ───────────────────────────────────────────────────────────

function ModeTab({ mode, active, icon, label, sublabel, onClick }: {
  mode: InputMode;
  active: boolean;
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
}) {
  void mode;
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-center ${
        active
          ? 'border-teal-500 bg-teal-50 text-teal-700'
          : 'border-cream-200 bg-white text-chocolate-500 hover:border-cream-300 hover:bg-cream-50'
      }`}
    >
      <span className="text-lg">{icon}</span>
      <span className="text-xs font-semibold">{label}</span>
      <span className="text-xs opacity-70 leading-tight">{sublabel}</span>
    </button>
  );
}

// ─── Export helpers ───────────────────────────────────────────────────────────

function buildExportText(analysis: SessionAnalysis, clientName: string, sessionDate: string): string {
  const date = new Date(sessionDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const lines: string[] = [
    `SESSION INTELLIGENCE REPORT`,
    `Human Potential OS™`,
    ``,
    `Client: ${clientName}`,
    `Session Date: ${date}`,
    `Generated: ${new Date().toLocaleDateString()}`,
    ``,
    `─────────────────────────────────────────────────`,
    `DISCLAIMER: All observations are AI-generated coaching hypotheses for professional review.`,
    `Validate every insight with your client. This is not diagnosis, therapy, or clinical assessment.`,
    `─────────────────────────────────────────────────`,
    ``,
  ];

  if (analysis.breakthrough_zone?.area_of_deepest_leverage) {
    lines.push(`BREAKTHROUGH ZONE [${analysis.breakthrough_zone.confidence?.toUpperCase()}]`);
    lines.push(analysis.breakthrough_zone.area_of_deepest_leverage);
    lines.push(analysis.breakthrough_zone.reasoning || '');
    if (analysis.breakthrough_zone.evidence?.length) {
      analysis.breakthrough_zone.evidence.forEach((e: string) => lines.push(`  • ${e}`));
    }
    if (analysis.breakthrough_zone.suggested_next_step) {
      lines.push(`Suggested next step: ${analysis.breakthrough_zone.suggested_next_step}`);
    }
    lines.push('');
  }

  if (analysis.session_summary) {
    const s = analysis.session_summary;
    lines.push(`SESSION SUMMARY`);
    lines.push(s.overview || '');
    if (s.main_topic) lines.push(`Main Topic: ${s.main_topic}`);
    if (s.client_goal) lines.push(`Client Goal: ${s.client_goal}`);
    if (s.key_moments?.length) {
      lines.push(`Key Moments:`);
      s.key_moments.forEach((m: string, i: number) => lines.push(`  ${i + 1}. ${m}`));
    }
    if (s.agreed_actions?.length) {
      lines.push(`Agreed Actions:`);
      s.agreed_actions.forEach((a: string) => lines.push(`  • ${a}`));
    }
    lines.push('');
  }

  const addSection = (title: string, items: unknown[], formatter: (item: unknown) => string[]) => {
    if (!items?.length) return;
    lines.push(title.toUpperCase());
    items.forEach((item, i) => {
      if (i > 0) lines.push('');
      formatter(item).forEach(l => lines.push(l));
    });
    lines.push('');
  };

  addSection('Key Themes', analysis.key_themes || [], (t: unknown) => {
    const theme = t as { theme: string; evidence: string; possible_importance: string; confidence: string };
    return [`${theme.theme} [${theme.confidence}]`, theme.evidence ? `  "${theme.evidence}"` : '', theme.possible_importance || ''].filter(Boolean);
  });

  addSection('Limiting Beliefs', analysis.limiting_beliefs || [], (b: unknown) => {
    const belief = b as { client_quote: string; possible_belief: string; severity: string; coaching_exploration: string; confidence: string };
    return [`"${belief.client_quote}" [${belief.severity}, ${belief.confidence}]`, `Possible belief: ${belief.possible_belief}`, belief.coaching_exploration ? `Coach: ${belief.coaching_exploration}` : ''].filter(Boolean);
  });

  addSection('Meta Model Patterns', analysis.meta_model_patterns || [], (p: unknown) => {
    const pattern = p as { client_quote: string; pattern_type: string; explanation: string; coach_question: string; confidence: string };
    return [`${pattern.pattern_type?.replace(/_/g, ' ').toUpperCase()} [${pattern.confidence}]`, `"${pattern.client_quote}"`, pattern.explanation || '', pattern.coach_question ? `Coach: ${pattern.coach_question}` : ''].filter(Boolean);
  });

  addSection('Emotional Language Signals', analysis.emotional_language_signals || [], (e: unknown) => {
    const signal = e as { emotion_or_theme: string; client_quote: string; severity: string; possible_coaching_direction: string; confidence: string };
    return [`${signal.emotion_or_theme} [${signal.severity}, ${signal.confidence}]`, signal.client_quote ? `"${signal.client_quote}"` : '', signal.possible_coaching_direction || ''].filter(Boolean);
  });

  addSection('Parts in Conflict', analysis.parts_conflicts || [], (p: unknown) => {
    const conflict = p as { part_a: string; part_b: string; evidence_from_transcript: string; coaching_exploration: string; confidence: string };
    return [`Part A: ${conflict.part_a}  |  Part B: ${conflict.part_b} [${conflict.confidence}]`, conflict.evidence_from_transcript ? `"${conflict.evidence_from_transcript}"` : '', conflict.coaching_exploration ? `Coach: ${conflict.coaching_exploration}` : ''].filter(Boolean);
  });

  addSection('Contradictions & Tensions', analysis.contradictions_and_tensions || [], (c: unknown) => {
    const contradiction = c as { tension: string; evidence_1: string; evidence_2: string; possible_meaning: string; coach_exploration: string; confidence: string };
    return [`${contradiction.tension} [${contradiction.confidence}]`, `1. "${contradiction.evidence_1}"`, `2. "${contradiction.evidence_2}"`, contradiction.possible_meaning || '', contradiction.coach_exploration ? `Coach: ${contradiction.coach_exploration}` : ''].filter(Boolean);
  });

  addSection('Identity Statements', analysis.identity_statements || [], (s: unknown) => {
    const stmt = s as { client_quote: string; identity_frame: string; possible_reframe_direction: string; confidence: string };
    return [`"${stmt.client_quote}" [${stmt.confidence}]`, stmt.identity_frame || '', stmt.possible_reframe_direction ? `Reframe: ${stmt.possible_reframe_direction}` : ''].filter(Boolean);
  });

  addSection('Values & Motivation Clues', analysis.values_and_motivation_clues || [], (v: unknown) => {
    const value = v as { value_or_motivation: string; evidence: string; possible_importance: string; coach_question: string; confidence: string };
    return [`${value.value_or_motivation} [${value.confidence}]`, value.evidence ? `"${value.evidence}"` : '', value.possible_importance || '', value.coach_question ? `Coach: ${value.coach_question}` : ''].filter(Boolean);
  });

  addSection('Avoidance & Resistance Patterns', analysis.avoidance_or_resistance_patterns || [], (a: unknown) => {
    const pattern = a as { pattern: string; evidence: string; possible_function: string; coach_exploration: string; confidence: string };
    return [`${pattern.pattern?.replace(/_/g, ' ').toUpperCase()} [${pattern.confidence}]`, pattern.evidence ? `"${pattern.evidence}"` : '', pattern.possible_function || '', pattern.coach_exploration ? `Coach: ${pattern.coach_exploration}` : ''].filter(Boolean);
  });

  if (analysis.suggested_coach_questions?.length) {
    lines.push('SUGGESTED COACH QUESTIONS');
    analysis.suggested_coach_questions.forEach((q: string, i: number) => lines.push(`  ${i + 1}. "${q}"`));
    lines.push('');
  }

  if (analysis.suggested_next_session_focus) {
    lines.push('SUGGESTED NEXT SESSION FOCUS');
    lines.push(analysis.suggested_next_session_focus);
    lines.push('');
  }

  if (analysis.important_cautions?.length) {
    lines.push('IMPORTANT CAUTIONS');
    analysis.important_cautions.forEach((c: string) => lines.push(`  • ${c}`));
    lines.push('');
  }

  return lines.join('\n');
}

function downloadText(text: string, filename: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main panel ──────────────────────────────────────────────────────────────

const AUDIO_ACCEPT = 'audio/mpeg,audio/mp4,audio/wav,audio/x-wav,audio/m4a,audio/x-m4a,audio/ogg,audio/webm';
const DOCUMENT_ACCEPT = 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,.pdf,.docx,.txt';

import {
  WHISPER_LIMIT_BYTES, CHUNK_SIZE_BYTES as _CHUNK_SIZE_BYTES,
  CONTAINER_FORMATS, decodeToWavChunks, sliceAudioFile,
} from '../../utils/audioChunking';


function estimateProcessingTime(file: File): string {
  const mb = file.size / (1024 * 1024);
  if (mb < 10) return '~1 min';
  if (mb < 50) return '~2–3 min';
  return '~5–8 min';
}

export function SessionIntelligencePanel({ session, coachId, clientName, clientGoals, reportsUsed, onClose, onUpdate, onReportUsed }: Props) {
  const [inputMode, setInputMode] = useState<InputMode>('audio');
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [showTranscript, setShowTranscript] = useState(false);
  const [resultsTab, setResultsTab] = useState<ResultsTab>('analysis');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const docRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const hasAnalysis = session.analysis_status === 'complete' && session.session_analysis;
  const isProcessing = session.analysis_status === 'processing';
  const currentStep: Step = hasAnalysis ? 'results' : isProcessing ? 'processing' : 'upload';

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (inputMode === 'audio' && f.size > 200 * 1024 * 1024) {
      setError('Audio file must be under 200 MB.');
      return;
    }
    if (inputMode === 'document' && f.size > 50 * 1024 * 1024) {
      setError('Document must be under 50 MB.');
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleModeChange = (mode: InputMode) => {
    setInputMode(mode);
    setFile(null);
    setConsent(false);
    setError(null);
  };

  const handleAnalyze = async () => {
    if (inputMode !== 'notes_only' && !file) {
      setError(`Please select a ${inputMode === 'audio' ? 'recording' : 'document'} file.`);
      return;
    }
    if (inputMode !== 'notes_only' && !consent) {
      setError('Please confirm consent before uploading.');
      return;
    }
    if (inputMode === 'notes_only' && !session.notes) {
      setError('No session notes found. Add notes to this session first.');
      return;
    }

    setError(null);

    if (typeof reportsUsed === 'number' && reportsUsed >= 1) {
      setError('You have used your 1 free report. Please upgrade to continue generating Session Intelligence reports.');
      return;
    }

    onUpdate(session.id, { analysis_status: 'processing' });

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;

      let transcript = '';
      let inputType: 'audio_transcript' | 'transcript' | 'notes_only' = 'notes_only';
      let audioDeleted = false;

      // ── PHASE 1: Upload + Transcribe ──────────────────────────────────────
      if (file && inputMode !== 'notes_only') {
        const isAudio = inputMode === 'audio';
        const ext = file.name.split('.').pop() || (isAudio ? 'mp3' : 'txt');

        if (isAudio && file.size > WHISPER_LIMIT_BYTES) {
          // Large audio: split into chunks, transcribe each separately.
          // Container formats (M4A, MP4, OGG, WebM) can't be byte-split because
          // the metadata box lives at the end of the file. Decode to WAV instead.
          const fileExt = file.name.split('.').pop()?.toLowerCase() || 'mp3';
          const useWav = CONTAINER_FORMATS.has(fileExt);
          let chunks: Blob[];
          let chunkExt: string;
          if (useWav) {
            setStatus('Decoding audio...');
            chunks = await decodeToWavChunks(file);
            chunkExt = 'wav';
          } else {
            chunks = sliceAudioFile(file);
            chunkExt = fileExt;
          }
          const transcriptParts: string[] = [];

          for (let i = 0; i < chunks.length; i++) {
            setStatus(`Uploading segment ${i + 1} of ${chunks.length}...`);
            const chunkPath = `${coachId}/${session.id}/chunk_${i}_${Date.now()}.${chunkExt}`;

            const { error: uploadErr } = await supabase.storage
              .from('session-recordings')
              .upload(chunkPath, chunks[i], { upsert: true });
            if (uploadErr) throw new Error(`Chunk upload failed: ${uploadErr.message}`);

            setStatus(`Transcribing segment ${i + 1} of ${chunks.length}...`);
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/session-intelligence`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                action: 'transcribe',
                storagePath: chunkPath,
                sessionId: i === chunks.length - 1 ? session.id : undefined,
                autoDeleteAudio: true,
              }),
            });
            if (!res.ok) {
              const body = await res.json().catch(() => ({ error: 'Unknown error' }));
              throw new Error(body.error || `Transcription failed: ${res.status}`);
            }
            const { transcript: part } = await res.json();
            transcriptParts.push(part);

            // Clean up chunk regardless of transcription outcome
            await supabase.storage.from('session-recordings').remove([chunkPath]);
          }

          transcript = transcriptParts.join('\n\n');
          inputType = 'audio_transcript';
          audioDeleted = true;

        } else {
          // Standard single-file upload + transcribe
          setStatus('Uploading file...');
          const path = `${coachId}/${session.id}/${Date.now()}.${ext}`;
          const { error: uploadError } = await supabase.storage
            .from('session-recordings')
            .upload(path, file, { upsert: true });
          if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

          const partialUpdates: Partial<CoachingSession> = {
            consent_confirmed: true,
            session_input_type: inputMode,
          };
          if (isAudio) partialUpdates.recording_url = path;
          else partialUpdates.session_document_url = path;
          onUpdate(session.id, partialUpdates);

          setStatus(isAudio ? 'Transcribing audio...' : 'Extracting text from document...');

          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/session-intelligence`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              action: 'transcribe',
              storagePath: path,
              sessionId: session.id,
              autoDeleteAudio: true,
            }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(body.error || `Transcription failed: ${res.status}`);
          }
          const result = await res.json();
          transcript = result.transcript;
          audioDeleted = result.audioDeleted;
          inputType = isAudio ? 'audio_transcript' : 'transcript';
        }

        onUpdate(session.id, {
          transcript,
          consent_confirmed: true,
          session_input_type: inputMode,
          ...(audioDeleted ? { recording_url: null, audio_auto_deleted: true } : {}),
        });
      }

      // ── PHASE 2: Analyse ──────────────────────────────────────────────────
      setStatus('Analysing session...');

      const analyzeRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/session-intelligence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: 'analyze',
          transcript: transcript || null,
          clientName,
          sessionDate: session.session_date,
          sessionType: session.session_type,
          sessionNotes: session.notes || '',
          clientGoals: clientGoals || '',
          sessionId: session.id,
          inputType,
        }),
      });

      if (!analyzeRes.ok) {
        const body = await analyzeRes.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(body.error || `Analysis failed: ${analyzeRes.status}`);
      }

      const { analysis } = await analyzeRes.json();

      const { error: dbError } = await supabase
        .from('coaching_sessions')
        .update({
          session_analysis: analysis,
          analysis_status: 'complete',
          consent_confirmed: inputMode !== 'notes_only',
          session_input_type: inputMode,
        })
        .eq('id', session.id);
      if (dbError) throw new Error(dbError.message);

      // Increment report usage count
      await supabase
        .from('coaches')
        .update({ si_reports_used: (reportsUsed ?? 0) + 1 } as Record<string, unknown>)
        .eq('id', coachId);
      onReportUsed?.();

      onUpdate(session.id, {
        transcript: transcript || null,
        session_analysis: analysis as SessionAnalysis,
        analysis_status: 'complete',
        consent_confirmed: inputMode !== 'notes_only',
        session_input_type: inputMode,
        audio_auto_deleted: audioDeleted,
        recording_url: audioDeleted ? null : session.recording_url,
      });

      setStatus('');
    } catch (err: unknown) {
      setError((err as Error).message || 'Analysis failed. Please try again.');
      onUpdate(session.id, { analysis_status: 'error' });
      setStatus('');
    }
  };

  const handleDeleteAnalysis = async () => {
    if (!confirm('Delete the full Session Intelligence analysis for this session?')) return;
    const paths: string[] = [];
    if (session.recording_url) paths.push(session.recording_url);
    if (session.session_document_url) paths.push(session.session_document_url);
    if (paths.length) await supabase.storage.from('session-recordings').remove(paths);

    await supabase.from('coaching_sessions').update({
      transcript: null,
      session_analysis: null,
      analysis_status: 'idle',
      recording_url: null,
      session_document_url: null,
      consent_confirmed: false,
      audio_auto_deleted: false,
    }).eq('id', session.id);

    onUpdate(session.id, {
      transcript: null,
      session_analysis: null,
      analysis_status: 'idle',
      recording_url: null,
      session_document_url: null,
      consent_confirmed: false,
      audio_auto_deleted: false,
    });
  };

  const handleDownload = () => {
    if (!session.session_analysis) return;
    const text = buildExportText(session.session_analysis, clientName, session.session_date);
    const safeName = clientName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const dateStr = session.session_date.replace(/-/g, '');
    downloadText(text, `session_intelligence_${safeName}_${dateStr}.txt`);
  };

  const handleSendChat = async () => {
    const message = chatInput.trim();
    if (!message || chatLoading || !session.session_analysis) return;

    setChatInput('');
    setChatError(null);
    const newMessages: ChatMessage[] = [...chatMessages, { role: 'coach', content: message }];
    setChatMessages(newMessages);
    setChatLoading(true);

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/session-intelligence`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'chat',
          clientName,
          sessionDate: session.session_date,
          transcript: session.transcript || null,
          analysis: session.session_analysis,
          messages: newMessages,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(body.error || `Chat failed: ${res.status}`);
      }

      const { reply } = await res.json();
      setChatMessages(prev => [...prev, { role: 'ai', content: reply }]);
    } catch (err: unknown) {
      setChatError((err as Error).message || 'Chat failed. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  const analysis = session.session_analysis;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-end z-50">
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-cream-200 bg-cream-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-500 rounded-xl">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-chocolate-900">Session Intelligence<sup className="text-[65%] align-super">™</sup></h2>
              <p className="text-xs text-chocolate-500">
                {clientName} · {new Date(session.session_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasAnalysis && (
              <>
                <button
                  onClick={handleDownload}
                  className="p-2 text-chocolate-400 hover:text-teal-600 rounded-lg hover:bg-cream-100 transition-colors"
                  title="Download analysis"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDeleteAnalysis}
                  className="p-2 text-chocolate-400 hover:text-red-600 rounded-lg hover:bg-cream-100 transition-colors"
                  title="Delete analysis"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 text-chocolate-400 hover:text-chocolate-700 rounded-lg hover:bg-cream-100 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* ── UPLOAD STEP ────────────────────────────────────────── */}
          {currentStep === 'upload' && (
            <div className="space-y-5">
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl">
                <p className="text-sm font-semibold text-teal-800 mb-1">Session Intelligence<sup className="text-[65%] align-super">™</sup></p>
                <p className="text-xs text-teal-700 leading-relaxed">
                  Analyses your session to identify possible limiting beliefs, Meta Model language patterns, emotional signals, parts conflicts, contradictions, identity-level statements, values clues, avoidance patterns, and breakthrough opportunities. All findings are coaching hypotheses for you to validate — not diagnosis or clinical assessment.
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold text-chocolate-600 mb-2 uppercase tracking-wide">Session input</p>
                <div className="flex gap-2">
                  <ModeTab mode="audio" active={inputMode === 'audio'} icon={<Mic className="w-4 h-4" />} label="Audio Recording" sublabel="MP3, M4A, WAV, OGG · up to 200 MB" onClick={() => handleModeChange('audio')} />
                  <ModeTab mode="document" active={inputMode === 'document'} icon={<File className="w-4 h-4" />} label="Document / Transcript" sublabel="PDF, Word (.docx), TXT" onClick={() => handleModeChange('document')} />
                  <ModeTab mode="notes_only" active={inputMode === 'notes_only'} icon={<FileText className="w-4 h-4" />} label="Notes Only" sublabel="Lower confidence" onClick={() => handleModeChange('notes_only')} />
                </div>
              </div>

              {inputMode === 'audio' && (
                <div className="space-y-3">
                  <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-cream-300 rounded-xl p-6 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50 transition-colors">
                    {file ? (
                      <div className="flex items-center justify-center gap-2 text-teal-700">
                        <Mic className="w-5 h-5" />
                        <span className="text-sm font-medium">{file.name}</span>
                        <span className="text-xs text-chocolate-400">({(file.size / 1024 / 1024).toFixed(1)} MB · {estimateProcessingTime(file)})</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-chocolate-300 mx-auto mb-2" />
                        <p className="text-sm text-chocolate-500">Click to select audio recording</p>
                        <p className="text-xs text-chocolate-400 mt-1">MP3, M4A, WAV, OGG, WebM · up to 200 MB</p>
                      </div>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept={AUDIO_ACCEPT} onChange={handleFileChange} className="hidden" />
                  <div className="flex items-center gap-2 p-3 bg-cream-50 border border-cream-200 rounded-lg text-xs text-chocolate-500">
                    <Shield className="w-3.5 h-3.5 flex-shrink-0 text-teal-500" />
                    Original audio is automatically deleted after transcription. Only the transcript and report are retained.
                  </div>
                </div>
              )}

              {inputMode === 'document' && (
                <div className="space-y-3">
                  <div onClick={() => docRef.current?.click()} className="border-2 border-dashed border-cream-300 rounded-xl p-6 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50 transition-colors">
                    {file ? (
                      <div className="flex items-center justify-center gap-2 text-teal-700">
                        <File className="w-5 h-5" />
                        <span className="text-sm font-medium">{file.name}</span>
                        <span className="text-xs text-chocolate-400">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-chocolate-300 mx-auto mb-2" />
                        <p className="text-sm text-chocolate-500">Click to select transcript or document</p>
                        <p className="text-xs text-chocolate-400 mt-1">PDF, Word (.docx), Plain Text (.txt)</p>
                      </div>
                    )}
                  </div>
                  <input ref={docRef} type="file" accept={DOCUMENT_ACCEPT} onChange={handleFileChange} className="hidden" />
                </div>
              )}

              {inputMode === 'notes_only' && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Lower confidence mode</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Without a verbatim transcript, the AI will analyse your session notes with reduced confidence. Language patterns, Meta Model violations, and emotional signals require direct client wording to detect reliably.
                  </p>
                  {!session.notes && <p className="text-xs text-red-600 mt-2 font-medium">No session notes found. Add notes to this session first.</p>}
                </div>
              )}

              {inputMode !== 'notes_only' && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                  <p className="text-xs font-semibold text-amber-800">Before uploading, please confirm:</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Session Intelligence™ will transcribe and analyse your coaching session to identify possible coaching patterns, limiting beliefs, emotional language, contradictions, parts conflicts, NLP language patterns, and suggested next-session questions.
                  </p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    This tool does not diagnose, treat, or replace professional judgement. It provides coaching hypotheses for the coach to review and validate with the client.
                  </p>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" id="consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-4 h-4 mt-0.5 accent-teal-600 flex-shrink-0" />
                    <label htmlFor="consent" className="text-xs text-amber-800 leading-relaxed cursor-pointer">
                      I confirm that I have permission to upload this session {inputMode === 'audio' ? 'audio' : 'document'} for transcription and AI-assisted coaching analysis, and I understand that the report provides coaching hypotheses only, not diagnosis, therapy, legal, medical, or psychological advice.
                    </label>
                  </div>
                </div>
              )}

              <button
                onClick={handleAnalyze}
                disabled={
                  (inputMode !== 'notes_only' && (!file || !consent)) ||
                  (inputMode === 'notes_only' && !session.notes)
                }
                className="freud-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Brain className="w-4 h-4" />
                {inputMode === 'audio' ? 'Transcribe & Analyse' : inputMode === 'document' ? 'Extract & Analyse' : 'Analyse Session Notes'}
              </button>

              <p className="text-xs text-chocolate-400 text-center">
                AI observations are coaching hypotheses for professional review only. Validate all insights with your client.
              </p>
            </div>
          )}

          {/* ── PROCESSING STEP ────────────────────────────────────── */}
          {currentStep === 'processing' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="p-4 bg-teal-100 rounded-full">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-chocolate-800">{status || 'Processing session...'}</p>
                <p className="text-xs text-chocolate-500 mt-1">
                  {status?.startsWith('Transcrib') ? 'Step 1 of 2 — transcription may take a minute for longer recordings.' :
                   status?.startsWith('Analysi') ? 'Step 2 of 2 — generating your coaching intelligence report.' :
                   'This may take a few minutes depending on session length.'}
                </p>
              </div>
            </div>
          )}

          {/* ── RESULTS STEP ───────────────────────────────────────── */}
          {currentStep === 'results' && analysis && (
            <div className="space-y-4">

              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-cream-100 rounded-xl border border-cream-200">
                <button
                  onClick={() => setResultsTab('analysis')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${resultsTab === 'analysis' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  Analysis
                </button>
                <button
                  onClick={() => setResultsTab('chat')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${resultsTab === 'chat' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Discuss with AI
                </button>
              </div>

              {/* ── ANALYSIS TAB ── */}
              {resultsTab === 'analysis' && (
                <div className="space-y-4">
                  {/* Analysis confidence banner */}
                  {analysis.analysis_confidence && (
                    <div className={`p-3 rounded-xl border flex items-start gap-3 ${
                      analysis.analysis_confidence.overall_confidence === 'high'
                        ? 'bg-sage-50 border-sage-200'
                        : analysis.analysis_confidence.overall_confidence === 'medium'
                        ? 'bg-teal-50 border-teal-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}>
                      <Shield className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        analysis.analysis_confidence.overall_confidence === 'high' ? 'text-sage-600'
                        : analysis.analysis_confidence.overall_confidence === 'medium' ? 'text-teal-600'
                        : 'text-amber-600'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-semibold text-chocolate-700">Analysis Confidence</span>
                          <ConfidenceBadge confidence={analysis.analysis_confidence.overall_confidence} />
                          <span className="text-xs text-chocolate-400 capitalize">
                            · {analysis.analysis_confidence.input_type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {analysis.analysis_confidence.limitations && (
                          <p className="text-xs text-chocolate-500 leading-relaxed">{analysis.analysis_confidence.limitations}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Coaching hypotheses disclaimer */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                      All observations are AI-generated coaching hypotheses for professional review. Validate every insight with your client before drawing conclusions. This is not diagnosis, therapy, or clinical assessment.
                    </p>
                  </div>

                  {/* ── Breakthrough Zone ─────────────────── */}
                  {analysis.breakthrough_zone?.area_of_deepest_leverage && (
                    <div className="p-4 bg-teal-700 rounded-xl shadow-md">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-teal-200" />
                          <p className="text-xs font-bold text-teal-100 uppercase tracking-wider">Breakthrough Zone</p>
                        </div>
                        <ConfidenceBadge confidence={analysis.breakthrough_zone.confidence} dark />
                      </div>
                      <p className="text-sm font-bold text-white mb-2 leading-snug">{analysis.breakthrough_zone.area_of_deepest_leverage}</p>
                      <p className="text-xs text-teal-100 leading-relaxed mb-3">{analysis.breakthrough_zone.reasoning}</p>
                      {analysis.breakthrough_zone.evidence?.length > 0 && (
                        <ul className="space-y-1 mb-3">
                          {analysis.breakthrough_zone.evidence.map((e: string, i: number) => (
                            <li key={i} className="text-xs text-teal-200 flex items-start gap-1.5">
                              <ArrowRight className="w-3 h-3 text-teal-300 mt-0.5 flex-shrink-0" />{e}
                            </li>
                          ))}
                        </ul>
                      )}
                      {analysis.breakthrough_zone.suggested_next_step && (
                        <div className="p-3 bg-white/15 rounded-lg border border-white/20">
                          <p className="text-xs font-semibold text-teal-100 mb-1">Suggested next step</p>
                          <p className="text-xs text-white leading-relaxed">{analysis.breakthrough_zone.suggested_next_step}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Session Summary ── */}
                  <Section title="Session Summary" icon={<FileText className="w-4 h-4" />} defaultOpen={true}>
                    <p className="text-sm text-chocolate-700 leading-relaxed">{analysis.session_summary?.overview}</p>
                    <div className="grid grid-cols-1 gap-2 mt-2">
                      <div className="p-2 bg-cream-100 rounded-lg">
                        <p className="text-xs font-semibold text-chocolate-500 mb-0.5">Main Topic</p>
                        <p className="text-sm text-chocolate-700">{analysis.session_summary?.main_topic}</p>
                      </div>
                      <div className="p-2 bg-sage-50 rounded-lg">
                        <p className="text-xs font-semibold text-sage-600 mb-0.5">Client Goal</p>
                        <p className="text-sm text-chocolate-700">{analysis.session_summary?.client_goal}</p>
                      </div>
                    </div>
                    {analysis.session_summary?.key_moments?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-chocolate-500 mb-1.5">Key Moments</p>
                        <ul className="space-y-1.5">
                          {analysis.session_summary.key_moments.map((m: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-chocolate-700">
                              <span className="w-5 h-5 bg-teal-100 text-teal-700 rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {analysis.session_summary?.agreed_actions?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-chocolate-500 mb-1.5">Agreed Actions</p>
                        <ul className="space-y-1">
                          {analysis.session_summary.agreed_actions.map((a: string, i: number) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-chocolate-700">
                              <CheckCircle className="w-4 h-4 text-sage-500 flex-shrink-0 mt-0.5" />{a}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Section>

                  {/* ── Key Themes ── */}
                  {analysis.key_themes?.length > 0 && (
                    <Section title="Key Themes" icon={<Layers className="w-4 h-4" />} count={analysis.key_themes.length} defaultOpen={true}>
                      <div className="space-y-3">
                        {analysis.key_themes.map((t: { theme: string; evidence: string; possible_importance: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-sm font-semibold text-chocolate-800">{t.theme}</p>
                              <ConfidenceBadge confidence={t.confidence} />
                            </div>
                            {t.evidence && <p className="text-xs text-chocolate-500 italic mb-1">"{t.evidence}"</p>}
                            {t.possible_importance && <p className="text-xs text-chocolate-500">{t.possible_importance}</p>}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Limiting Beliefs ── */}
                  {analysis.limiting_beliefs?.length > 0 && (
                    <Section title="Limiting Beliefs" icon={<Brain className="w-4 h-4" />} count={analysis.limiting_beliefs.length}>
                      <div className="space-y-3">
                        {analysis.limiting_beliefs.map((b: { client_quote: string; possible_belief: string; severity: SeverityLevel; coaching_exploration: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <p className="text-xs text-chocolate-500 italic">"{b.client_quote}"</p>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <SeverityBadge severity={b.severity} />
                                <ConfidenceBadge confidence={b.confidence} />
                              </div>
                            </div>
                            <p className="text-sm font-medium text-chocolate-800 mb-1">{b.possible_belief}</p>
                            {b.coaching_exploration && (
                              <p className="text-xs text-teal-700 italic flex items-start gap-1">
                                <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {b.coaching_exploration}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Meta Model Patterns ── */}
                  {analysis.meta_model_patterns?.length > 0 && (
                    <Section title="Meta Model Patterns" icon={<Eye className="w-4 h-4" />} count={analysis.meta_model_patterns.length} defaultOpen={false}>
                      <div className="space-y-3">
                        {analysis.meta_model_patterns.map((p: { client_quote: string; pattern_type: string; explanation: string; coach_question: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <span className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide bg-cream-200 px-2 py-0.5 rounded">
                                {p.pattern_type?.replace(/_/g, ' ')}
                              </span>
                              <ConfidenceBadge confidence={p.confidence} />
                            </div>
                            <p className="text-sm text-chocolate-700 italic mb-1">"{p.client_quote}"</p>
                            <p className="text-xs text-chocolate-500 mb-1.5">{p.explanation}</p>
                            {p.coach_question && (
                              <p className="text-xs text-teal-700 italic flex items-start gap-1">
                                <HelpCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {p.coach_question}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Emotional Language Signals ── */}
                  {analysis.emotional_language_signals?.length > 0 && (
                    <Section title="Emotional Language Signals" icon={<Heart className="w-4 h-4" />} count={analysis.emotional_language_signals.length}>
                      <div className="space-y-3">
                        {analysis.emotional_language_signals.map((e: { emotion_or_theme: string; client_quote: string; severity: SeverityLevel; possible_coaching_direction: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <p className="text-sm font-semibold text-chocolate-800 capitalize">{e.emotion_or_theme}</p>
                              <div className="flex items-center gap-1.5">
                                <SeverityBadge severity={e.severity} />
                                <ConfidenceBadge confidence={e.confidence} />
                              </div>
                            </div>
                            {e.client_quote && <p className="text-xs text-chocolate-500 italic mb-1">"{e.client_quote}"</p>}
                            {e.possible_coaching_direction && (
                              <p className="text-xs text-teal-700 flex items-start gap-1">
                                <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {e.possible_coaching_direction}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Parts in Conflict ── */}
                  {analysis.parts_conflicts?.length > 0 && (
                    <Section title="Parts in Conflict" icon={<Users className="w-4 h-4" />} count={analysis.parts_conflicts.length} defaultOpen={false}>
                      <div className="space-y-3">
                        {analysis.parts_conflicts.map((p: { part_a: string; part_b: string; evidence_from_transcript: string; possible_positive_intentions: string; coaching_exploration: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <p className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide">Conflict</p>
                              <ConfidenceBadge confidence={p.confidence} />
                            </div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div className="p-2 bg-teal-50 rounded border border-teal-100">
                                <p className="text-xs font-semibold text-teal-700 mb-0.5">Part A</p>
                                <p className="text-xs text-chocolate-700">{p.part_a}</p>
                              </div>
                              <div className="p-2 bg-amber-50 rounded border border-amber-100">
                                <p className="text-xs font-semibold text-amber-700 mb-0.5">Part B</p>
                                <p className="text-xs text-chocolate-700">{p.part_b}</p>
                              </div>
                            </div>
                            {p.evidence_from_transcript && <p className="text-xs text-chocolate-500 italic mb-1">"{p.evidence_from_transcript}"</p>}
                            {p.possible_positive_intentions && <p className="text-xs text-chocolate-500 mb-1">{p.possible_positive_intentions}</p>}
                            {p.coaching_exploration && (
                              <p className="text-xs text-teal-700 italic flex items-start gap-1">
                                <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {p.coaching_exploration}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Contradictions and Tensions ── */}
                  {analysis.contradictions_and_tensions?.length > 0 && (
                    <Section title="Contradictions & Tensions" icon={<AlertTriangle className="w-4 h-4" />} count={analysis.contradictions_and_tensions.length} defaultOpen={false}>
                      <div className="space-y-3">
                        {analysis.contradictions_and_tensions.map((c: { tension: string; evidence_1: string; evidence_2: string; possible_meaning: string; coach_exploration: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <p className="text-sm font-semibold text-chocolate-800">{c.tension}</p>
                              <ConfidenceBadge confidence={c.confidence} />
                            </div>
                            <div className="space-y-1 mb-1.5">
                              <p className="text-xs text-chocolate-500 italic flex items-start gap-1"><span className="text-teal-400 font-bold">1.</span>"{c.evidence_1}"</p>
                              <p className="text-xs text-chocolate-500 italic flex items-start gap-1"><span className="text-amber-500 font-bold">2.</span>"{c.evidence_2}"</p>
                            </div>
                            {c.possible_meaning && <p className="text-xs text-chocolate-600 mb-1">{c.possible_meaning}</p>}
                            {c.coach_exploration && (
                              <p className="text-xs text-teal-700 italic flex items-start gap-1">
                                <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {c.coach_exploration}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Identity Statements ── */}
                  {analysis.identity_statements?.length > 0 && (
                    <Section title="Identity-Level Statements" icon={<BookOpen className="w-4 h-4" />} count={analysis.identity_statements.length} defaultOpen={false}>
                      <div className="space-y-3">
                        {analysis.identity_statements.map((s: { client_quote: string; identity_frame: string; possible_reframe_direction: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <p className="text-sm text-chocolate-700 italic">"{s.client_quote}"</p>
                              <ConfidenceBadge confidence={s.confidence} />
                            </div>
                            <p className="text-xs text-chocolate-500 mb-1">{s.identity_frame}</p>
                            {s.possible_reframe_direction && (
                              <p className="text-xs text-teal-700 flex items-start gap-1">
                                <TrendingUp className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {s.possible_reframe_direction}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Values & Motivation Clues ── */}
                  {analysis.values_and_motivation_clues?.length > 0 && (
                    <Section title="Values & Motivation Clues" icon={<Target className="w-4 h-4" />} count={analysis.values_and_motivation_clues.length} defaultOpen={false}>
                      <div className="space-y-3">
                        {analysis.values_and_motivation_clues.map((v: { value_or_motivation: string; evidence: string; possible_importance: string; coach_question: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-sm font-semibold text-chocolate-800 capitalize">{v.value_or_motivation}</p>
                              <ConfidenceBadge confidence={v.confidence} />
                            </div>
                            {v.evidence && <p className="text-xs text-chocolate-500 italic mb-1">"{v.evidence}"</p>}
                            {v.possible_importance && <p className="text-xs text-chocolate-500 mb-1">{v.possible_importance}</p>}
                            {v.coach_question && (
                              <p className="text-xs text-teal-700 italic flex items-start gap-1">
                                <HelpCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {v.coach_question}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Avoidance & Resistance Patterns ── */}
                  {analysis.avoidance_or_resistance_patterns?.length > 0 && (
                    <Section title="Avoidance & Resistance Patterns" icon={<Eye className="w-4 h-4" />} count={analysis.avoidance_or_resistance_patterns.length} defaultOpen={false} accent="text-amber-600">
                      <div className="space-y-3">
                        {analysis.avoidance_or_resistance_patterns.map((a: { pattern: string; evidence: string; possible_function: string; coach_exploration: string; confidence: ConfidenceLevel }, i: number) => (
                          <div key={i} className="p-3 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                                {a.pattern?.replace(/_/g, ' ')}
                              </span>
                              <ConfidenceBadge confidence={a.confidence} />
                            </div>
                            {a.evidence && <p className="text-xs text-chocolate-500 italic mb-1">"{a.evidence}"</p>}
                            {a.possible_function && <p className="text-xs text-chocolate-500 mb-1">{a.possible_function}</p>}
                            {a.coach_exploration && (
                              <p className="text-xs text-teal-700 italic flex items-start gap-1">
                                <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {a.coach_exploration}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* ── Next Session Prep ── */}
                  <Section title="Next Session Prep" icon={<Target className="w-4 h-4" />} defaultOpen={true}>
                    {analysis.next_session_prep?.suggested_focus && (
                      <div className="p-3 bg-teal-50 rounded-lg border border-teal-100 mb-2">
                        <p className="text-xs font-semibold text-teal-700 mb-0.5">Suggested Focus</p>
                        <p className="text-sm text-chocolate-700">{analysis.next_session_prep.suggested_focus}</p>
                      </div>
                    )}
                    {analysis.next_session_prep?.risks_to_avoid?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-red-600 mb-1.5">Risks to Avoid</p>
                        <ul className="space-y-1">
                          {analysis.next_session_prep.risks_to_avoid.map((r: string, i: number) => (
                            <li key={i} className="text-sm text-chocolate-700 flex items-start gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />{r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Section>

                  {/* ── Suggested Coach Questions ── */}
                  {analysis.suggested_coach_questions?.length > 0 && (
                    <Section title="Suggested Coach Questions" icon={<MessageSquare className="w-4 h-4" />} count={analysis.suggested_coach_questions.length}>
                      <p className="text-xs text-chocolate-500 mb-3">Questions based on what the client actually said, for use in the next session:</p>
                      <ol className="space-y-2">
                        {analysis.suggested_coach_questions.map((q: string, i: number) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-chocolate-700">
                            <span className="w-5 h-5 bg-teal-100 text-teal-700 rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-medium">{i + 1}</span>
                            <span className="italic">"{q}"</span>
                          </li>
                        ))}
                      </ol>
                    </Section>
                  )}

                  {/* ── Suggested Next Session Focus ── */}
                  {analysis.suggested_next_session_focus && (
                    <div className="p-4 bg-sage-50 border border-sage-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-1.5">
                        <TrendingUp className="w-4 h-4 text-sage-600" />
                        <p className="text-xs font-bold text-sage-700 uppercase tracking-wide">Suggested Next Session Focus</p>
                      </div>
                      <p className="text-sm text-chocolate-800">{analysis.suggested_next_session_focus}</p>
                    </div>
                  )}

                  {/* ── Important Cautions ── */}
                  {analysis.important_cautions?.length > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Important Cautions</p>
                      </div>
                      <ul className="space-y-1.5">
                        {analysis.important_cautions.map((c: string, i: number) => (
                          <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                            <span className="text-amber-500 mt-0.5">·</span>{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ── Transcript viewer ── */}
                  {session.transcript && (
                    <div>
                      <button
                        onClick={() => setShowTranscript(t => !t)}
                        className="flex items-center gap-2 text-xs text-chocolate-500 hover:text-chocolate-700 transition-colors"
                      >
                        {showTranscript ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {showTranscript ? 'Hide transcript' : 'View session transcript'}
                      </button>
                      <AnimatePresence>
                        {showTranscript && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-3 p-4 bg-cream-50 rounded-xl border border-cream-200 max-h-64 overflow-y-auto">
                              <p className="text-xs text-chocolate-600 whitespace-pre-wrap leading-relaxed">{session.transcript}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {session.audio_auto_deleted && (
                    <div className="flex items-center gap-2 text-xs text-chocolate-400 p-2 bg-cream-50 rounded-lg border border-cream-200">
                      <Shield className="w-3.5 h-3.5 text-teal-500" />
                      Original audio deleted · Transcript retained
                    </div>
                  )}
                </div>
              )}

              {/* ── CHAT TAB ── */}
              {resultsTab === 'chat' && (
                <div className="flex flex-col" style={{ minHeight: '500px' }}>
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl mb-4">
                    <p className="text-xs font-semibold text-teal-800 mb-0.5">AI Thinking Partner</p>
                    <p className="text-xs text-teal-700 leading-relaxed">
                      Discuss the analysis, explore hypotheses, plan your next session, or bounce ideas. The AI has full context of this session's report.
                    </p>
                  </div>

                  {chatMessages.length === 0 && (
                    <div className="space-y-2 mb-4">
                      {[
                        'What do you think is the most important pattern to address first?',
                        'How should I approach the breakthrough zone in the next session?',
                        'What questions would you prioritise from the suggested list?',
                      ].map((prompt, i) => (
                        <button
                          key={i}
                          onClick={() => { setChatInput(prompt); }}
                          className="w-full text-left px-3 py-2.5 text-xs text-chocolate-600 bg-cream-50 hover:bg-cream-100 border border-cream-200 rounded-lg transition-colors"
                        >
                          "{prompt}"
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex-1 space-y-3 mb-4 overflow-y-auto max-h-80">
                    {chatMessages.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === 'coach' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] px-3 py-2.5 rounded-xl text-xs leading-relaxed ${
                          msg.role === 'coach'
                            ? 'bg-teal-600 text-white rounded-br-sm'
                            : 'bg-cream-100 text-chocolate-800 border border-cream-200 rounded-bl-sm'
                        }`}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-cream-100 border border-cream-200 px-3 py-2.5 rounded-xl rounded-bl-sm flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 bg-chocolate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {chatError && (
                    <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {chatError}
                      <button onClick={() => setChatError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                      placeholder="Ask about the analysis or bounce an idea..."
                      disabled={chatLoading}
                      className="flex-1 freud-input text-sm disabled:opacity-60"
                    />
                    <button
                      onClick={handleSendChat}
                      disabled={!chatInput.trim() || chatLoading}
                      className="p-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
